import {
  type Account,
  type PlatformType,
  type PublishablePlatformType,
  platformTypes,
  publishablePlatformTypes,
} from '@backend/lib/db/schema/platform'
import { type SelectedAccount } from '@/components/workspace/workspace-header/workspace-header-social-accounts'
import { type PlatformCompatibility } from './workspace-media-validation'

export type ValidationWarning = {
  platform: PlatformType
  message: string
}

// Accounts + auth status are publishable-only (generation targets like Meta Ads
// have no connected account).
export type PlatformAccountsMap = Record<PublishablePlatformType, Account[]>
export type PlatformAuthMap = Record<PublishablePlatformType, boolean>

const PLATFORM_NAME: Record<PlatformType, string> = {
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  facebook: 'Facebook',
  'meta-ads': 'Meta Ads',
}

export const getPlatformName = (platform: PlatformType): string => PLATFORM_NAME[platform]

const getAccountIssue = (
  platform: PlatformType,
  isAuthenticated: boolean,
  accounts: Account[],
  selectedAccounts: SelectedAccount[],
): ValidationWarning | null => {
  const platformName = getPlatformName(platform)

  if (!isAuthenticated) {
    return {
      platform,
      message: `To post on ${platformName} you need to connect your account first.`,
    }
  }
  if (accounts.length === 0) {
    return {
      platform,
      message: `No ${platformName} accounts available. Please reconnect your account.`,
    }
  }
  if (!selectedAccounts.find((acc) => acc.platform === platform)) {
    return {
      platform,
      message: `Select a ${platformName} account.`,
    }
  }
  return null
}

/**
 * Validates the workspace preview and returns the compatibility, warnings, and whether to show the preview
 */
export const validateWorkspacePreview = (options: {
  contentCompatibility: PlatformCompatibility
  selectedPlatform: PlatformType
  selectedAccounts: SelectedAccount[]
  isAuthenticated: PlatformAuthMap
  accounts: PlatformAccountsMap
  activePlatforms: PlatformType[]
}) => {
  // Compute account-related issues for every active platform.
  // Account checks only apply to publishable platforms — generation-only targets
  // (e.g. Meta Ads) have no account to connect, so they never get a warning here.
  const accountIssues: Partial<Record<PlatformType, ValidationWarning>> = {}
  for (const platform of publishablePlatformTypes) {
    if (!options.activePlatforms.includes(platform)) continue
    const issue = getAccountIssue(
      platform,
      options.isAuthenticated[platform],
      options.accounts[platform],
      options.selectedAccounts,
    )
    if (issue) accountIssues[platform] = issue
  }

  // Combine content + account compatibility into a single map.
  const compatibility = Object.fromEntries(
    platformTypes.map((platform) => [
      platform,
      {
        compatible: options.contentCompatibility[platform].compatible && !accountIssues[platform],
        reason: options.contentCompatibility[platform].reason,
      },
    ]),
  ) as PlatformCompatibility

  const warnings: ValidationWarning[] = []

  if (options.activePlatforms.includes(options.selectedPlatform)) {
    const selectedContent = options.contentCompatibility[options.selectedPlatform]
    const selectedAccountIssue = accountIssues[options.selectedPlatform]

    if (!selectedContent.compatible && selectedContent.reason) {
      warnings.push({ platform: options.selectedPlatform, message: selectedContent.reason })
    }

    if (selectedAccountIssue) {
      warnings.push(selectedAccountIssue)
    }
  }

  // NOTE: now we trust warning messages string maybe structure this better?
  const isAccountWarning = (message: string) =>
    message.startsWith('Select a') || message.startsWith('To post on') || message.startsWith('No ')

  const hasOnlyAccountWarnings =
    warnings.length > 0 && warnings.every((w) => isAccountWarning(w.message))

  const showPreview = warnings.length === 0 || hasOnlyAccountWarnings

  return { compatibility, warnings, showPreview }
}
